import { FetchResult } from '@apollo/client';
import {
  CategoryTranslationDeleteMutation,
  CategoryTranslationUpsertMutation,
  useCategoryTranslationDeleteMutation,
  useCategoryTranslationUpsertMutation,
} from 'lib-knowledge-management-common/graphql';
import { FormState } from 'react-hook-form';

import useCategoryMove from 'hooks/useCategoryMove';

import {
  CategoryEditModalSchema,
  CategoryParentRef,
} from './categoryEditModalSchema';

type UseCategoryTranslationUpsertReturn = [
  (formValues: CategoryEditModalSchema) => void,
  { saveDisabled: boolean },
];
type UseCategoryTranslationUpsertParams = {
  parentRef: CategoryParentRef;
  formState: FormState<CategoryEditModalSchema>;
  categoryId?: string;
  onCompleted: () => void;
  onMoveError: (error: Error) => void;
  onTitleUpsertError: (error: Error) => void;
  onTitleDeleteError: (error: Error) => void;
};

export function useCategoryUpdate({
  formState,
  parentRef,
  categoryId,
  onCompleted,
  onMoveError,
  onTitleUpsertError,
  onTitleDeleteError,
}: UseCategoryTranslationUpsertParams): UseCategoryTranslationUpsertReturn {
  const [categoryTitleUpsertMutation, { loading: updating }] =
    useCategoryTranslationUpsertMutation();
  const [categoryTitleDeleteMutation, { loading: deleting }] =
    useCategoryTranslationDeleteMutation();
  const [moveMutation, { loading: moving }] = useCategoryMove({
    onError: onMoveError,
  });

  const onSubmit = async (formValues: CategoryEditModalSchema) => {
    if (!categoryId) return; // Bail early if not categoryId

    const { titles } = formValues;
    const categoryTitlePromises: Array<
      Promise<
        | FetchResult<CategoryTranslationUpsertMutation>
        | FetchResult<CategoryTranslationDeleteMutation>
      >
    > = [];

    const updatedTitles = titles.filter((_, index) => {
      const titleValueHasUpdated =
        formState.dirtyFields.titles?.[index] || false;
      return titleValueHasUpdated;
    });

    updatedTitles.forEach((title) => {
      if (title.title) {
        const titleUpsertPromise = categoryTitleUpsertMutation({
          variables: {
            id: categoryId,
            title: title.title,
            languageId: title.languageId,
          },
          onError: onTitleUpsertError,
        });
        categoryTitlePromises.push(titleUpsertPromise);
      } else {
        const titleDeletionPromise = categoryTitleDeleteMutation({
          variables: {
            id: categoryId,
            languageId: title.languageId,
          },
          onError: onTitleDeleteError,
        });
        categoryTitlePromises.push(titleDeletionPromise);
      }
    });

    await Promise.all(categoryTitlePromises);
    const shouldMove = parentRef?.id !== formState.defaultValues?.parentRef?.id;

    if (shouldMove && parentRef && categoryId) {
      await moveMutation({
        categoryId,
        newParentRef: parentRef,
      });
    }
    onCompleted();
  };

  const saveDisabled =
    moving || updating || deleting || !formState.isDirty || !formState.isValid;

  return [onSubmit, { saveDisabled }];
}
